import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { useBranding, resolveImageUrl } from '@/contexts/BrandingContext';
import { Building2, ArrowLeft, CalendarDays, Eye, User } from 'lucide-react';

export default function PostDetailPage() {
  const { id: postId } = useParams();
  const navigate = useNavigate();
  const { branding: brandData } = useBranding();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPost = async () => {
      try {
        const res = await api.get(`/posts/${postId}`);
        setPost(res.data);
      } catch (e) {
        navigate('/publicaciónes');
      } finally {
        setLoading(false);
      }
    };
    loadPost();
  }, [postId, navigate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-PY', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F4F0] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#264639] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      <nav className="fixed top-0 left-0 right-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              {brandData?.logo_url ? (
                <img src={resolveImageUrl(brandData.logo_url)} alt="" className="w-10 h-10 rounded-lg object-contain" />
              ) : (
                <div className="w-10 h-10 bg-[#264639] rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
              )}
              <span className="font-bold text-xl text-[#1C1917]" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
                {brandData?.site_name || 'Estadía 365'}
              </span>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/publicaciónes" className="text-sm text-[#57534E] hover:text-[#264639] flex items-center gap-1 mb-6">
            <ArrowLeft className="w-4 h-4" /> Volver a publicaciones
          </Link>

          {post.image_url && (
            <div className="rounded-2xl overflow-hidden shadow-lg mb-8">
              <img src={post.image_url} alt={post.title} className="w-full h-[350px] object-cover" />
            </div>
          )}

          <article className="bg-white rounded-xl p-6 sm:p-8 shadow-sm" data-testid="post-detail">
            <div className="flex flex-wrap items-center gap-3 text-sm text-[#78716C] mb-4">
              {post.category && (
                <span className="bg-[#DCE9E2] text-[#264639] px-2.5 py-0.5 rounded-full text-xs font-medium uppercase tracking-wider">
                  {post.category}
                </span>
              )}
              <span className="flex items-center gap-1">
                <CalendarDays className="w-3.5 h-3.5" /> {formatDate(post.created_at)}
              </span>
              {post.author && (
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" /> {post.author}
                </span>
              )}
              {post.views > 0 && (
                <span className="flex items-center gap-1">
                  <Eye className="w-3.5 h-3.5" /> {post.views} lecturas
                </span>
              )}
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1917] mb-6" style={{ fontFamily: 'var(--font-heading, Outfit)' }} data-testid="post-title">
              {post.title}
            </h1>

            <div
              className="prose prose-stone max-w-none text-[#57534E] leading-relaxed whitespace-pre-line"
              data-testid="post-content"
            >
              {post.content}
            </div>

            {post.tags && post.tags.length > 0 && (
              <div className="mt-8 pt-6 border-t border-[#E7E5E4] flex flex-wrap gap-2">
                {post.tags.map(tag => (
                  <span key={tag} className="bg-[#F5F4F0] text-[#57534E] px-3 py-1 rounded-full text-xs">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </article>
        </div>
      </main>
    </div>
  );
}
